import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useConfirmedMutation } from '../../lib/confirm';
import { Modal, Select, Tag, Alert, Input, Typography, Button, Tooltip, message } from '../../ui';
import { request } from '../../api/client';
import { fmtQty } from '../../lib/format';
import { warehouseText, warehouseLabel, MasterTag } from '../../lib/warehouseLabel';
import { useAnyPermission, useCanSeeWarehouses, usePermission } from '../../store/auth';
import { inr2 } from '../../lib/format';
import { PALETTE } from '../../lib/palette';
import { OrderStockLines } from './OrderStockLines';

/**
 * Approve an order AND assign its fulfillment (dispatch) warehouse in one step. The chosen
 * warehouse's manager then packs + dispatches it. Shows live per-warehouse availability against the
 * order's lines; if the warehouse is short, the admin can still approve (the shortfall backorders)
 * or "Approve & create transfers" to consolidate stock into that warehouse first.
 */
export function ApproveOrderModal({ open, onClose, orderId, orderNo, defaultWarehouseId, onApproved }: {
  open: boolean;
  onClose: () => void;
  orderId: string | null;
  orderNo?: string | null;
  defaultWarehouseId?: string | null;
  onApproved?: () => void;
}) {
  const canSeeWarehouses = useCanSeeWarehouses();
  const qc = useQueryClient();
  // Creating transfers (consolidation) is an ops action — only offer it to approvers who can do it.
  const canConsolidate = useAnyPermission(['ORDER.DISPATCH.PLAN', 'ORDER.EDIT']);
  const [wid, setWid] = useState<string | undefined>();
  const [notes, setNotes] = useState('');

  // Prefill the warehouse on open: fresh approvals start empty; a re-approval (e.g. BACKORDER→APPROVED)
  // starts on the order's existing fulfillment warehouse so it isn't silently reassigned.
  useEffect(() => {
    if (open) { setWid(defaultWarehouseId ? String(defaultWarehouseId) : undefined); setNotes(''); }
  }, [open, defaultWarehouseId]);

  const { data: warehouses } = useQuery({
    queryKey: ['warehouses'],
    queryFn: () => request<any[]>({ url: '/warehouses/my' }),
    // Gated so a role without warehouse access doesn't fire a call it is refused — see useCanSeeWarehouses.
    enabled: canSeeWarehouses && open,
  });
  const { data: plan } = useQuery({
    queryKey: ['order-stock-plan', orderId, wid],
    queryFn: () => request<any>({ url: `/orders/${orderId}/stock-plan`, params: wid ? { warehouseId: wid } : {} }),
    enabled: open && !!orderId && !!wid,
  });
  // Prepaid / advance context — surfaced so the approver knows money is committed (prioritise fulfilment)
  // and whether an advance payment still needs verifying. Read-only; does NOT gate approval.
  const { data: advanceCd } = useQuery({
    queryKey: ['advance-cd', orderId],
    queryFn: () => request<any>({ url: `/orders/${orderId}/advance-cd` }),
    enabled: open && !!orderId,
  });
  const { data: orderPayments } = useQuery({
    queryKey: ['order-payments', orderId],
    queryFn: () => request<any[]>({ url: `/orders/${orderId}/payments` }),
    enabled: open && !!orderId,
  });
  const advPaid = (orderPayments ?? []).filter((p: any) => p.approvalStatus === 'APPROVED' && p.status !== 'REFUNDED').reduce((s: number, p: any) => s + Number(p.amount || 0), 0);
  const advPending = (orderPayments ?? []).filter((p: any) => p.approvalStatus === 'PENDING').reduce((s: number, p: any) => s + Number(p.amount || 0), 0);
  const cdStatus: string | undefined = advanceCd?.status;
  const isPrepaid = cdStatus === 'ESTIMATED' || cdStatus === 'LOCKED' || advPaid > 0 || advPending > 0;
  /*
   * "Prepaid order" is only true once the money in covers what prepaying costs. An estimated CD is an
   * OFFER, and a part payment (or an auto-consumed credit of ₹404 against ₹8,653) is not prepayment —
   * the approver was being told the order was paid for when it was not.
   */
  const prepayTarget = Number(advanceCd?.netPayable ?? 0) > 0 ? Number(advanceCd.netPayable) : Number(advanceCd?.original?.grand ?? 0);
  const fullyPrepaid = advPaid > 0.01 && prepayTarget > 0 && advPaid >= prepayTarget - 0.01;
  const moneyHeadline = fullyPrepaid
    ? 'Prepaid order'
    : advPaid > 0.01 ? `Part paid — ${inr2(advPaid)} of ${inr2(prepayTarget)}`
    : advPending > 0.01 ? `Payment awaiting verification — ${inr2(advPending)}`
    : 'Cash discount offered — nothing paid yet';

  const close = () => { setWid(undefined); setNotes(''); onClose(); };

  const approve = useConfirmedMutation({ title: "Approve this order?", content: "A proforma is issued and the credit clock starts. Stock is committed at the warehouse you picked.", okText: "Yes, approve" }, {
    mutationFn: async (opts: { consolidate?: boolean }) => {
      await request({ url: `/orders/${orderId}/approve`, method: 'POST', data: { warehouseId: wid, notes: notes.trim() || undefined } });
      // Approve has committed. Consolidation is a best-effort SECOND step — caught so a transfer
      // failure never re-runs /approve (which would dead-end on "APPROVED→APPROVED").
      let consolidateError: string | null = null;
      if (opts.consolidate) {
        try {
          await request({ url: `/orders/${orderId}/consolidate`, method: 'POST', data: { toWarehouseId: wid } });
        } catch (e: any) {
          consolidateError = e?.response?.data?.error?.message ?? 'creating transfers failed';
        }
      }
      return { consolidateError };
    },
    onSuccess: (res: { consolidateError: string | null }) => {
      if (res.consolidateError) {
        message.warning(`Order approved — but creating transfers failed: ${res.consolidateError}. Use the order's Stock Planning to consolidate.`);
      } else {
        message.success('Order approved & warehouse assigned');
      }
      qc.invalidateQueries({ queryKey: ['orders'] });
      qc.invalidateQueries({ queryKey: ['orders-pending-approval'] });
      qc.invalidateQueries({ queryKey: ['order', orderId] });
      qc.invalidateQueries({ queryKey: ['badges'] });
      onApproved?.();
      close();
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Approval failed'),
  });

  const lines: any[] = plan?.lines ?? [];
  const short = !!wid && !!plan && !plan.fullyAvailableHere;
  const totalShort = lines.some((l) => Number(l.availableTotal ?? 0) < Number(l.pendingQty ?? 0)); // even all-WH can't cover

  // "CODE · Name" + a gold "master" capsule on the master warehouse (shared picker helper). `text` stays a
  // plain string so type-to-search (optionFilterProp="text") still works with the rich node label.
  // Live preview of the tax head for the warehouse currently selected (never for the stored one).
  // Gated on the permission exactly as the order page's determination card is: without it the
  // endpoint answers 403, and firing it anyway put a guaranteed refusal in the network log of every
  // approval instead of simply not showing the panel.
  const canSeeGst = usePermission('GST.DETERMINATION.VIEW');
  const { data: gst } = useQuery({
    queryKey: ['gst-determination-preview', orderId, wid],
    queryFn: () => request<any>({ url: `/gst/orders/${orderId}/determination`, params: { warehouseId: wid } }),
    enabled: open && canSeeGst && !!orderId && !!wid,
    retry: false,
  });
  /* The server re-determines GST at approval and REFUSES when it cannot (GST_DETERMINATION_BLOCKED) — for this
     warehouse every approve button can only fail, after the Yes/No. The preview already knows, so the buttons do
     too: disabled, with the reason in the warning above them (DEF-069, 15 Sep). */
  const gstBlocked = !!wid && !!gst?.engineEnabled && !gst?.taxNature;

  /*
   * The picker answers the question the approver is actually asking — "which of these can ship THIS
   * order?" — instead of listing names and leaving them to select one, read the table, and select the
   * next. Coverage for every warehouse is already computed by the order's coverage endpoint (shared
   * query key with the order page's panel, so opening the modal from there costs no extra request);
   * the options are grouped by it, best first. If the call is refused the picker degrades to the plain
   * name list rather than breaking approval.
   */
  const { data: coverage } = useQuery({
    queryKey: ['order-coverage', orderId],
    queryFn: () => request<any>({ url: `/orders/${orderId}/warehouse-coverage` }),
    enabled: open && !!orderId,
    retry: false,
  });
  const covByWh = new Map<string, any>((coverage?.warehouses ?? []).map((w: any) => [String(w.warehouseId), w]));
  const GROUPS: { key: string; title: string }[] = [
    { key: 'FULL', title: 'Covers this order in full' },
    { key: 'PARTIAL', title: 'Partly covered — the rest transfers in or backorders' },
    { key: 'NONE', title: 'No stock for this order here' },
    { key: 'UNKNOWN', title: 'Other warehouses' },
  ];
  const covTag = (c: any) => {
    if (!c) return null;
    if (c.coverage === 'FULL') {
      // "Full" on stock already owed to other orders is a half-truth — say which it is.
      return Number(c.totalCommitted) > 0 && !c.freeCovers
        ? <Tag tone="gold" style={{ margin: 0 }}>full · committed</Tag>
        : <Tag tone="green" style={{ margin: 0 }}>full</Tag>;
    }
    if (c.coverage === 'PARTIAL') return <Tag tone="gold" style={{ margin: 0 }}>{fmtQty(c.totalFulfillable)} of {fmtQty(c.totalRequired)}</Tag>;
    return <Tag style={{ margin: 0 }}>no stock</Tag>;
  };
  const whOptions = GROUPS.map((g) => ({
    label: g.title,
    options: (warehouses ?? [])
      .filter((w: any) => (covByWh.get(String(w.id))?.coverage ?? 'UNKNOWN') === g.key)
      .map((w: any) => {
        const c = covByWh.get(String(w.id));
        return {
          value: String(w.id),
          // Searchable text carries the city too, so "pune" finds it by place as well as by code.
          text: [warehouseText(w), w.city].filter(Boolean).join(' · '),
          // Trigger label stays one compact line; the rich two-line row below is dropdown-only.
          label: warehouseLabel(w),
          wh: w,
          cov: c,
        };
      }),
  })).filter((g) => g.options.length > 0);

  return (
    <Modal
      open={open}
      onOpenChange={(o) => { if (!o) close(); }}
      title={`Approve order${orderNo ? ` · ${orderNo}` : ''}`}
      // The coverage table carries up to SIX columns. Undeclared columns reserve a readable default
      // (150px each), so at 700px the table was 900px wide inside a ~650px dialog and scrolled
      // sideways — taking the product name, the one column that says WHICH line the numbers belong
      // to, out of view. Widened here and tightened per column below, per the modal-table rule.
      width={900}
      footer={[
        <Button key="cancel" variant="default" onClick={close}>Cancel</Button>,
        short && !totalShort && canConsolidate ? (
          <Button key="consolidate" variant="default" onClick={() => approve.mutate({ consolidate: true })} loading={approve.isPending} disabled={!wid || gstBlocked}>
            Approve &amp; create transfers
          </Button>
        ) : null,
        <Button key="approve" variant="primary" onClick={() => approve.mutate({})} loading={approve.isPending} disabled={!wid || gstBlocked}>
          {short ? 'Approve anyway' : 'Approve'}
        </Button>,
      ]}
    >
      {isPrepaid && (
        <Alert
          type={fullyPrepaid ? 'success' : advPaid > 0 || advPending > 0 ? 'warning' : 'info'}
          style={{ marginBottom: 12 }}
          title={
            <span>
              {moneyHeadline}{' '}
              {cdStatus === 'LOCKED'
                ? <Tag tone="green" style={{ margin: 0 }}>Advance CD locked</Tag>
                : cdStatus === 'ESTIMATED'
                  ? <Tag tone="gold" style={{ margin: 0 }}>Advance CD estimated</Tag>
                  : null}
            </span>
          }
          description={
            <div style={{ fontSize: 13 }}>
              {(cdStatus === 'ESTIMATED' || cdStatus === 'LOCKED') && advanceCd?.netPayable != null && (
                <div>Advance cash discount {cdStatus === 'LOCKED' ? 'locked' : '(estimated)'} — net payable <b>{inr2(advanceCd.netPayable)}</b>{advanceCd?.original?.grand != null && <> (was {inr2(advanceCd.original.grand)})</>}.</div>
              )}
              {advPaid > 0
                ? <div style={{ color: 'var(--color-success-600)' }}>✓ Advance {inr2(advPaid)} received &amp; verified.</div>
                : advPending > 0
                  ? <div style={{ color: 'var(--color-warning-600)' }}>Advance {inr2(advPending)} submitted — pending Accounts verification.</div>
                  : <div style={{ color: 'var(--gb-text-muted)' }}>No advance payment received yet.</div>}
              <div style={{ color: 'var(--gb-text-muted)', fontSize: 12, marginTop: 2 }}>
                Money is committed — consider prioritising fulfilment. (The discount is locked by Accounts later, before billing; approval doesn’t change it.)
              </div>
            </div>
          }
        />
      )}

      <Alert type="info" style={{ marginBottom: 12 }}
        title="Assign the fulfillment warehouse"
        description="The warehouse you pick packs & dispatches this order (its manager sees it in their queue). Pick one that holds the stock — or approve and create transfers to bring stock in; anything still short is backordered." />

      <Typography.Text strong>Fulfillment warehouse <span style={{ color: PALETTE.danger[500] }}>*</span></Typography.Text>
      {/* Sized to the field, not to the dialog: at 100% of a 900px modal the list was a wall of
          whitespace with six words in it. */}
      <Select showSearch optionFilterProp="text"
        placeholder="Select warehouse" style={{ width: 460, maxWidth: '100%', margin: '4px 0 10px' }}
        value={wid} onChange={setWid} options={whOptions}
        optionRender={(o: any) => (
          <div className="flex w-full items-center gap-3">
            <span className="min-w-0 flex-1">
              <span className="flex items-center truncate text-content">{o.wh?.name ?? o.text}{o.wh?.isMaster ? <MasterTag /> : null}</span>
              <span className="block truncate text-xs text-muted">
                {[o.wh?.code, o.wh?.city].filter(Boolean).join(' · ')}
              </span>
            </span>
            {/*
              * NOBODY WORKS HERE.
              *
              * A warehouse with no manager assigned can be picked at approval and then never accept
              * the order — it holds no queue, shows no fulfilment action to anyone, and the order sits
              * APPROVED for ever. Seen live: an order assigned to Delhi, which has no manager, left
              * the warehouse manager looking at a page with no buttons.
              *
              * Flagged rather than hidden: the warehouse may still be the right commercial answer
              * (it holds the stock), and the fix is to staff it — a decision for a human, not a
              * filter that quietly removes the option.
              */}
            {o.cov && Number(o.cov.managerCount ?? 0) === 0 && (
              <Tooltip label="No warehouse manager is assigned here, so nobody can accept or pack this order. Assign a manager first, or pick another warehouse.">
                <Tag tone="red" style={{ margin: 0 }}>no manager</Tag>
              </Tooltip>
            )}
            {covTag(o.cov)}
          </div>
        )} />

      {/*
        * WHAT IS ON THE ORDER, and whether this warehouse can ship it.
        *
        * The SAME panel the approvals hub's decision dialog renders (OrderStockLines), so an order
        * approved from Sales → Order approval and the same order approved from the Approvals hub are
        * decided on identical facts. The GST band slots into the middle of it, exactly where it has
        * always sat: the tax head and the stock position are both consequences of the warehouse just
        * chosen, and they belong together above the lines.
        */}
      {wid && orderId && (
        <OrderStockLines
          orderId={orderId}
          warehouseId={wid}
          beforeTable={/* THE TAX CONSEQUENCE OF THIS CHOICE. The warehouse decides which GST registration supplies the
              order, and therefore whether the dealer is billed CGST+SGST or IGST. The approver is making
              that choice right here, so the answer belongs here rather than being discovered afterwards
              on the order screen. Previewed against the selected warehouse — nothing is committed until
              Approve. */
            gst?.engineEnabled ? (
              gst.taxNature ? (
                <div className="mb-2 flex flex-wrap items-center gap-x-2 gap-y-1 rounded-card border border-line px-3 py-2 text-sm">
                  <b className="text-content">
                    {gst.taxNature === 'INTER_STATE' ? 'Inter-state · IGST'
                      : gst.taxNature === 'INTRA_UT' ? 'Intra-union-territory · CGST + UTGST'
                      : 'Intra-state · CGST + SGST'}
                  </b>
                  <span className="text-muted">
                    supplied from {gst.supplierStateName ?? gst.supplierStateCode ?? '—'}
                    {' → '}
                    place of supply {gst.placeOfSupplyStateName ?? gst.placeOfSupplyStateCode ?? '—'}
                  </span>
                </div>
              ) : (
                <Alert type="warning" style={{ marginBottom: 8 }}
                  title="GST cannot be determined for this warehouse"
                  description={`${(gst.blockers ?? []).map((b: any) => b.message).join(' ') || 'The supplying registration is not established.'} This order cannot be approved from here — pick another warehouse, or have this one mapped to a GST registration first.`} />
              )
            ) : null}
        />
      )}

      <div style={{ marginTop: 10 }}>
        <Typography.Text type="secondary">Notes (optional)</Typography.Text>
        <Input.TextArea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Approval notes…" style={{ marginTop: 4 }} />
      </div>
    </Modal>
  );
}
